import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import MarketHeader from "@/components/MarketHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtCurrency, fmtNum, pnlClass } from "@/lib/format";
import { ChartNoAxesCombined, X } from "lucide-react";
import { toast } from "sonner";

export default function Positions() {
  const [trades, setTrades] = useState([]);
  const [orders, setOrders] = useState([]);

  const load = () => {
    // Positions, Trade History and Order Log are displayed
    // using the current IST trading day.
    api.get("/trades/today").then((r) => setTrades(r.data)).catch(() => {});
    api.get("/orders/today").then((r) => setOrders(r.data)).catch(() => {});
  };
  useEffect(() => { load(); const t = setInterval(load, 1000); return () => clearInterval(t); }, []);

  const closeTrade = async (id) => {
    if (!window.confirm("Force-close this running trade at current LTP?")) return;
    try { await api.post(`/trades/${id}/close`); toast.success("Trade closed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openMtmChart = () => {
    window.open("/mtm-chart", "algodesk-mtm", "popup,width=1180,height=760,resizable=yes,scrollbars=yes");
  };

  const running = trades.filter((t) => t.status === "RUNNING");
  const completed = trades.filter((t) => t.status !== "RUNNING");

  return (
    <div className="flex flex-col min-h-screen" data-testid="positions-page">
      <MarketHeader />
      <div className="p-3 sm:p-6 space-y-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Positions & Orders</h1>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-2">
            <p className="text-sm text-slate-400">Live positions, executed trades, and full order log.</p>
            <Button size="sm" variant="outline" onClick={openMtmChart} data-testid="open-mtm-chart" className="border-blue-500/30 text-blue-300 hover:bg-blue-500/10 self-start sm:ml-auto">
              <ChartNoAxesCombined className="w-4 h-4 mr-1.5" /> Open MTM chart
            </Button>
          </div>
        </div>

        <Tabs defaultValue="positions" className="w-full">
          <TabsList className="bg-[#141414] border border-white/10 rounded-sm">
            <TabsTrigger value="positions" data-testid="tab-positions">Positions ({running.length})</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">Trade History ({completed.length})</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">Order Log ({orders.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="positions" className="mt-4">
            <Card className="border-white/10 bg-[#141414] rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="running-positions-table">
                  <thead className="bg-white/5 text-slate-500 uppercase tracking-widest text-[10px]">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Strategy</th>
                      <th className="text-left px-3 py-2 font-medium">Symbol</th>
                      <th className="text-left px-3 py-2 font-medium">Side</th>
                      <th className="text-right px-3 py-2 font-medium">Qty</th>
                      <th className="text-right px-3 py-2 font-medium">Entry</th>
                      <th className="text-right px-3 py-2 font-medium">LTP</th>
                      <th className="text-right px-3 py-2 font-medium">SL</th>
                      <th className="text-right px-3 py-2 font-medium">P&L</th>
                      <th className="text-center px-3 py-2 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {running.length === 0 && <tr><td colSpan="9" className="text-center py-8 text-slate-500">No open positions.</td></tr>}
                    {running.map((t) => t.legs.filter((l) => l.status === "OPEN").map((l, i) => (
                      <tr key={`${t.id}-${i}`} className="border-t border-white/5 hover:bg-white/5" data-testid={`position-row-${t.id}-${i}`}>
                        <td className="px-3 py-2">{t.strategy_name}</td>
                        <td className="px-3 py-2 font-mono text-slate-300">{l.symbol}</td>
                        <td className="px-3 py-2">
                          <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-mono ${l.side === "SELL" ? "bg-rose-500/15 text-rose-400" : "bg-emerald-500/15 text-emerald-400"}`}>{l.side} {l.kind}</span>
                        </td>
                        <td className="px-3 py-2 text-right font-mono">{l.quantity}</td>
                        <td className="px-3 py-2 text-right font-mono">{fmtNum(l.entry_price)}</td>
                        <td className="px-3 py-2 text-right font-mono text-blue-300">{fmtNum(l.ltp)}</td>
                        <td className="px-3 py-2 text-right font-mono text-amber-300">{fmtNum(l.sl_price)} {l.sl_moved_to_be && <span className="ml-1 text-[9px] px-1 py-0.5 rounded-sm bg-blue-500/20 text-blue-300">BE</span>}</td>
                        <td className={`px-3 py-2 text-right font-mono font-semibold ${pnlClass(l.pnl)}`}>{fmtCurrency(l.pnl)}</td>
                        <td className="px-3 py-2 text-center">
                          {i === 0 && (
                            <Button size="sm" variant="ghost" onClick={() => closeTrade(t.id)} data-testid={`close-trade-${t.id}`} className="text-rose-400 hover:text-rose-300 h-7">
                              <X className="w-3.5 h-3.5 mr-1" /> Close
                            </Button>
                          )}
                        </td>
                      </tr>
                    )))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            <Card className="border-white/10 bg-[#141414] rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="history-table">
                  <thead className="bg-white/5 text-slate-500 uppercase tracking-widest text-[10px]">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Date</th>
                      <th className="text-left px-3 py-2 font-medium">Strategy</th>
                      <th className="text-left px-3 py-2 font-medium">Index</th>
                      <th className="text-left px-3 py-2 font-medium">Mode</th>
                      <th className="text-right px-3 py-2 font-medium">Legs</th>
                      <th className="text-left px-3 py-2 font-medium">Status</th>
                      <th className="text-right px-3 py-2 font-medium">P&L</th>
                    </tr>
                  </thead>
                  <tbody>
                    {completed.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-slate-500">No completed trades yet.</td></tr>}
                    {completed.map((t) => (
                      <tr key={t.id} className="border-t border-white/5 hover:bg-white/5">
                        <td className="px-3 py-2 font-mono text-slate-400">{t.trade_date}</td>
                        <td className="px-3 py-2">{t.strategy_name}</td>
                        <td className="px-3 py-2">{t.index}</td>
                        <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase ${t.trade_mode === "live" ? "bg-rose-500/15 text-rose-400" : "bg-amber-500/15 text-amber-400"}`}>{t.trade_mode}</span></td>
                        <td className="px-3 py-2 text-right font-mono">{t.legs.length}</td>
                        <td className="px-3 py-2"><span className="text-slate-400 text-[10px] uppercase tracking-widest">{t.status}</span></td>
                        <td className={`px-3 py-2 text-right font-mono font-semibold ${pnlClass(t.total_pnl)}`}>{fmtCurrency(t.total_pnl)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="orders" className="mt-4">
            <Card className="border-white/10 bg-[#141414] rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="orders-table">
                  <thead className="bg-white/5 text-slate-500 uppercase tracking-widest text-[10px]">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Time</th>
                      <th className="text-left px-3 py-2 font-medium">Action</th>
                      <th className="text-left px-3 py-2 font-medium">Symbol</th>
                      <th className="text-left px-3 py-2 font-medium">Side</th>
                      <th className="text-right px-3 py-2 font-medium">Qty</th>
                      <th className="text-right px-3 py-2 font-medium">Price</th>
                      <th className="text-left px-3 py-2 font-medium">Type</th>
                      <th className="text-left px-3 py-2 font-medium">Broker</th>
                      <th className="text-left px-3 py-2 font-medium">Status</th>
                      <th className="text-left px-3 py-2 font-medium">Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.length === 0 && <tr><td colSpan="10" className="text-center py-8 text-slate-500">No orders yet.</td></tr>}
                    {orders.map((o) => (
                      <tr key={o.id} className="border-t border-white/5 hover:bg-white/5">
                        <td className="px-3 py-2 font-mono text-slate-400">{new Date(o.timestamp).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).replace(",", "")}</td>
                        <td className="px-3 py-2 text-[10px] uppercase tracking-widest text-slate-300">{o.action}</td>
                        <td className="px-3 py-2 font-mono">{o.symbol}</td>
                        <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-mono ${o.side === "SELL" ? "bg-rose-500/15 text-rose-400" : "bg-emerald-500/15 text-emerald-400"}`}>{o.side} {o.leg}</span></td>
                        <td className="px-3 py-2 text-right font-mono">{o.quantity}</td>
                        <td className="px-3 py-2 text-right font-mono">{fmtNum(o.price)}</td>
                        <td className="px-3 py-2 text-slate-400">{o.order_type}</td>
                        <td className="px-3 py-2 text-slate-400">{o.broker}</td>
                        <td className="px-3 py-2"><span className={`text-[10px] uppercase tracking-widest ${o.status === "SUCCESS" ? "text-emerald-400" : "text-rose-400"}`}>{o.status}</span></td>
                        <td className="px-3 py-2 text-slate-500 text-[10px] truncate max-w-xs">{o.message || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

